'use client';

import * as React from 'react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { Cross2Icon } from '@radix-ui/react-icons';
import { isTouchViewport } from '@/hooks/use-mobile';
import { COARSE_HIT_AREA_CLASS } from '@/components/ui/touch-target';

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
	React.ElementRef<typeof SheetPrimitive.Overlay>,
	React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
	<SheetPrimitive.Overlay
		className={cn(
			'fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
			className
		)}
		{...props}
		ref={ref}
	/>
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
	'fixed z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out',
	{
		variants: {
			side: {
				top: 'inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top max-xl:max-h-[var(--app-height)] max-xl:overflow-y-auto max-xl:overscroll-contain',
				// Phones and medium screens: never taller than the visible area, and
				// it sits above the on-screen keyboard.
				bottom: 'inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom max-md:bottom-[var(--keyboard-inset,0px)] max-md:max-h-[calc(var(--app-height)-var(--safe-top)-var(--call-bar-height,0px)-1.5rem)] max-md:overflow-y-auto max-md:overscroll-contain max-md:rounded-t-2xl md:max-xl:max-h-[calc(var(--app-height)-1rem)] md:max-xl:overflow-y-auto md:max-xl:overscroll-contain',
				// Side sheets follow the visible height (it shrinks above the
				// keyboard on touch screens) and scroll inside when they're short.
				left: 'inset-y-0 left-0 h-full w-full border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-[600px] max-md:max-w-none max-xl:bottom-auto max-xl:h-[var(--app-height)] max-md:top-[var(--call-bar-height,0px)] max-md:h-[calc(var(--app-height)-var(--call-bar-height,0px))] max-xl:overflow-y-auto max-xl:overscroll-contain',
				right: 'inset-y-0 right-0 h-full w-full border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-[600px] max-md:w-full max-md:max-w-none max-xl:bottom-auto max-xl:h-[var(--app-height)] max-md:top-[var(--call-bar-height,0px)] max-md:h-[calc(var(--app-height)-var(--call-bar-height,0px))] max-xl:overflow-y-auto max-xl:overscroll-contain',
			},
		},
		defaultVariants: {
			side: 'right',
		},
	}
);

const zeroTop = /(?:^|\s)p[ty]?-0(?=\s|$)/;
const zeroBottom = /(?:^|\s)p[by]?-0(?=\s|$)/;

/**
 * Phone safe-area padding (notch and home indicator). A sheet that keeps the
 * default padding gets at least that much plus the inset. A caller that
 * passes p-0 lays out its own edges, so it gets nothing unless it asks with
 * safeArea={true}; safeArea={false} turns it off for any sheet.
 */
function sheetSafeArea(
	side: SheetContentProps['side'],
	className = '',
	safeArea: boolean | undefined
) {
	if (safeArea === false) return '';
	const ownTop = zeroTop.test(className);
	const ownBottom = zeroBottom.test(className);
	const top =
		side === 'bottom'
			? ''
			: ownTop
				? safeArea && 'max-md:pt-[var(--safe-top)]'
				: 'max-md:pt-[max(1.5rem,var(--safe-top))]';
	const bottom =
		side === 'top'
			? ''
			: ownBottom
				? safeArea && 'max-md:pb-[var(--safe-bottom)] max-md:kb-open:pb-0'
				: 'max-md:pb-[max(1.5rem,var(--safe-bottom))] max-md:kb-open:pb-6';
	return cn(top, bottom);
}

/**
 * Below 1280px, the sides of the screen a sheet touches clear the notch,
 * Dynamic Island and rounded corners of a landscape phone. A transparent
 * border as wide as the inset adds to whatever padding the caller uses (p-0
 * included) and moves the close button in with it. The insets are 0 in
 * portrait, on tablets and on desktop, so nothing changes there.
 */
const sheetSideInsets: Record<NonNullable<SheetContentProps['side']>, string> = {
	top: 'max-xl:border-x-transparent max-xl:border-l-[length:var(--safe-left)] max-xl:border-r-[length:var(--safe-right)]',
	bottom: 'max-xl:border-x-transparent max-xl:border-l-[length:var(--safe-left)] max-xl:border-r-[length:var(--safe-right)]',
	left: 'max-xl:border-l-transparent max-xl:border-l-[length:var(--safe-left)]',
	right: 'max-xl:border-r-transparent max-xl:border-r-[length:var(--safe-right)]',
};

interface SheetContentProps
	extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
		VariantProps<typeof sheetVariants> {
	// For sheets that draw their own header with a Done/Close button.
	hideClose?: boolean;
	overlayClassName?: string;
	/**
	 * Pad for the notch and the home indicator on phones. By default sheets
	 * with the standard padding get it and p-0 sheets (which lay out their
	 * own edges) don't; pass true or false to decide. The side insets of a
	 * landscape phone are always cleared (see sheetSideInsets).
	 */
	safeArea?: boolean;
}

const SheetContent = React.forwardRef<
	React.ElementRef<typeof SheetPrimitive.Content>,
	SheetContentProps
>(({ side = 'right', className, children, hideClose = false, overlayClassName, safeArea, onOpenAutoFocus, ...props }, ref) => (
	<SheetPortal>
		<SheetOverlay className={overlayClassName} />
		<SheetPrimitive.Content
			ref={ref}
			className={cn(
				sheetVariants({ side }),
				sheetSideInsets[side ?? 'right'],
				sheetSafeArea(side, className, safeArea),
				// Touch screens focus the sheet itself when it opens (below); it
				// needs no focus ring around the whole panel.
				'max-md:outline-none touch:outline-none',
				className
			)}
			onOpenAutoFocus={(event) => {
				if (onOpenAutoFocus) return onOpenAutoFocus(event);
				// Touch screens (phones in either orientation, tablets): focus
				// the sheet, not its first field, so the keyboard doesn't cover
				// a sheet the person has only just opened.
				if (isTouchViewport()) {
					event.preventDefault();
					(event.currentTarget as HTMLElement | null)?.focus({ preventScroll: true });
				}
			}}
			{...props}
		>
			{!hideClose && (
				<SheetPrimitive.Close
					className={cn(
						'absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary',
						// Phones: a 44px target, below the notch on full-height sheets.
						'max-md:right-1.5 max-md:rounded-full max-md:p-3 max-md:data-[state=open]:bg-transparent',
						// Tablets and landscape phones keep the small X, with a 44px hit area.
						COARSE_HIT_AREA_CLASS,
						side === 'bottom' ? 'max-md:top-1.5' : 'max-md:top-[calc(var(--safe-top)+0.375rem)]'
					)}
				>
					<Cross2Icon className="h-4 w-4 max-md:h-5 max-md:w-5" />
					<span className="sr-only">Close</span>
				</SheetPrimitive.Close>
			)}
			{children}
		</SheetPrimitive.Content>
	</SheetPortal>
));
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn(
			'flex flex-col space-y-2 text-center sm:text-left',
			className
		)}
		{...props}
	/>
);
SheetHeader.displayName = 'SheetHeader';

const SheetFooter = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn(
			'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
			className
		)}
		{...props}
	/>
);
SheetFooter.displayName = 'SheetFooter';

const SheetTitle = React.forwardRef<
	React.ElementRef<typeof SheetPrimitive.Title>,
	React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
	<SheetPrimitive.Title
		ref={ref}
		className={cn('text-lg font-semibold text-foreground', className)}
		{...props}
	/>
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
	React.ElementRef<typeof SheetPrimitive.Description>,
	React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
	<SheetPrimitive.Description
		ref={ref}
		className={cn('text-sm text-muted-foreground', className)}
		{...props}
	/>
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
	Sheet,
	SheetPortal,
	SheetOverlay,
	SheetTrigger,
	SheetClose,
	SheetContent,
	SheetHeader,
	SheetFooter,
	SheetTitle,
	SheetDescription,
};
